import { useContext, useMemo, useRef } from "react";
import { UseQueryResult } from "@tanstack/react-query";
import { useGetGapByCountyStatusQueries } from "@app/network-portfolio-management/services/npmQueries";
import { GapByCountyStatusResult, NetworkPortfolioManagementProject } from "@app/network-portfolio-management/interfaces";
import { UserContext } from "@app/user/UserContext";

interface UseGapByCountyStatusByPageResult {
  gapByStatusByProjectId: Map<number, GapByCountyStatusResult>;
  gapByStatusQueries: UseQueryResult<GapByCountyStatusResult[], Error>[];
  pageIndexByProjectId: Map<number, number>;
}

/**
 * Gap by County Status loads progressively, batched per already-fetched page rather than per-card/
 * per-row or across the whole accumulated list: each page's own project IDs are a stable query key,
 * so react-query never re-fetches page 1 when page 2 loads. Shared by both the card view
 * (ProjectInfiniteList) and the row/table view (ProjectTable) so the query-key/batching logic isn't
 * duplicated between the two.
 */
export const useGapByCountyStatusByPage = (pages: NetworkPortfolioManagementProject[][]): UseGapByCountyStatusByPageResult => {
  const userId = useRef(useContext(UserContext).userId).current;

  const pageProjectIds = useMemo(
    () => pages.map((page) => page.map((project) => project.projectId)),
    [pages],
  );

  const gapByStatusQueries = useGetGapByCountyStatusQueries(userId, pageProjectIds);

  const pageIndexByProjectId = useMemo(() => {
    const map = new Map<number, number>();
    pageProjectIds.forEach((ids, pageIndex) => ids.forEach((id) => map.set(id, pageIndex)));
    return map;
  }, [pageProjectIds]);

  const gapByStatusByProjectId = useMemo(() => {
    const map = new Map<number, GapByCountyStatusResult>();
    gapByStatusQueries.forEach((query) => query.data?.forEach((result) => map.set(result.projectId, result)));
    return map;
  }, [gapByStatusQueries]);

  return { gapByStatusByProjectId, gapByStatusQueries, pageIndexByProjectId };
};
